import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { getConsent, recordConsent, DEFAULT_PURPOSES } from "../lib/consent";
import { toast } from "sonner";

/**
 * PreferenceCenter — modal (shadcn Dialog) pour ajuster les finalités
 * cookies/traceurs. `necessary` toujours ON (grisé). Sauvegarde locale +
 * serveur (best-effort).
 */
export default function PreferenceCenter({ open, onOpenChange }) {
  const [purposes, setPurposes] = useState(DEFAULT_PURPOSES);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const existing = getConsent();
      setPurposes(existing?.purposes || DEFAULT_PURPOSES);
    }
  }, [open]);

  const toggle = (key) => {
    if (key === "necessary") return;
    setPurposes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await recordConsent(purposes);
      toast.success("Préférences enregistrées.");
      onOpenChange?.(false);
    } catch {
      toast.error("Impossible d'enregistrer les préférences.");
    } finally {
      setSaving(false);
    }
  };

  const rejectAll = async () => {
    setSaving(true);
    try {
      const p = { ...DEFAULT_PURPOSES, necessary: true };
      await recordConsent(p);
      setPurposes(p);
      toast.success("Seuls les cookies strictement nécessaires sont activés.");
      onOpenChange?.(false);
    } finally {
      setSaving(false);
    }
  };

  const acceptAll = async () => {
    setSaving(true);
    try {
      const p = { necessary: true, analytics: true, marketing: true, functional: true };
      await recordConsent(p);
      setPurposes(p);
      toast.success("Toutes les finalités activées.");
      onOpenChange?.(false);
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      key: "necessary",
      title: "Strictement nécessaires",
      desc: "Requis pour le fonctionnement du site (authentification, sécurité, préférences de session). Ne peuvent être désactivés.",
      locked: true,
    },
    {
      key: "functional",
      title: "Fonctionnels",
      desc: "Améliorent le confort (mémorisation de choix d'affichage, langue, consentements précédents).",
    },
    {
      key: "analytics",
      title: "Mesure d'audience",
      desc: "Statistiques anonymes de fréquentation, permet d'améliorer les contenus et parcours du site.",
    },
    {
      key: "marketing",
      title: "Communication & marketing",
      desc: "Contenus personnalisés, mesure de campagnes ITERCATUS. Aucun partage à des tiers publicitaires.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="preference-center-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Préférences cookies & traceurs
          </DialogTitle>
          <DialogDescription>
            Choisissez précisément ce que vous acceptez. Vos choix restent modifiables à tout moment depuis le pied de page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-4">
          {rows.map((r) => (
            <div
              key={r.key}
              className="flex items-start justify-between gap-4 rounded-lg border p-3"
              data-testid={`consent-row-${r.key}`}
            >
              <div className="flex-1">
                <p className="font-semibold text-sm">{r.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {r.desc}
                </p>
              </div>
              <Switch
                checked={!!purposes[r.key]}
                onCheckedChange={() => toggle(r.key)}
                disabled={r.locked || saving}
                data-testid={`consent-toggle-${r.key}`}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={rejectAll}
            disabled={saving}
            data-testid="consent-reject-all"
          >
            Tout refuser
          </Button>
          <Button
            variant="outline"
            onClick={acceptAll}
            disabled={saving}
            data-testid="consent-accept-all"
          >
            Tout accepter
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="consent-save"
          >
            {saving ? "Enregistrement…" : "Enregistrer mes choix"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
